import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import "./CustomMultiSelect.css";

/**
 * CustomMultiSelect Component
 * A custom multiselect dropdown with search, checkboxes, and select all functionality
 * 
 * @param {Array} options - Array of options with {id, text} structure
 * @param {Array} value - Array of selected option IDs
 * @param {Function} onChange - Callback when selection changes (receives array of selected IDs)
 * @param {string} placeholder - Placeholder text
 * @param {boolean} disabled - Whether the dropdown is disabled
 * @param {boolean} loading - Show inline loader (e.g. while options fetch)
 * @param {string} loadingText - Text shown while loading (default: "Loading...")
 * @param {boolean} showSelectAll - Whether to show "Select all" option (default: true)
 * @param {string} selectAllLabel - Label for the select-all row (default: "Select all")
 * @param {boolean} singleSelect - Only one option at a time (radio-like; shows option label in field)
 * @param {string} className - Additional CSS classes
 */
const CustomMultiSelect = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select options",
  disabled = false,
  loading = false,
  loadingText = "Loading...",
  showSelectAll = true,
  selectAllLabel = "Select all",
  singleSelect = false,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const displayRef = useRef(null);
  const searchInputRef = useRef(null);
  const dropdownMenuRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = options.filter((option) =>
    option.text.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check if all visible options are selected
  useEffect(() => {
    if (filteredOptions.length > 0 && showSelectAll) {
      const allSelected = filteredOptions.every((option) =>
        value.includes(option.id)
      );
      setSelectAllChecked(allSelected);
    } else {
      setSelectAllChecked(false);
    }
  }, [value, filteredOptions, showSelectAll]);

  // Calculate dropdown position when opened
  useEffect(() => {
    if (isOpen && displayRef.current) {
      const updatePosition = () => {
        const rect = displayRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
          width: rect.width,
        });
      };

      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [isOpen]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        dropdownMenuRef.current &&
        !dropdownMenuRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    if (isOpen) {
      // Use setTimeout to avoid immediate closure
      setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 0);
      // Focus search input when dropdown opens
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Toggle dropdown
  const toggleDropdown = () => {
    if (!disabled && !loading) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm("");
      }
    }
  };

  // Handle option toggle
  const handleOptionToggle = (optionId) => {
    if (disabled) return;

    let newValue;
    if (singleSelect) {
      newValue = value.includes(optionId) ? [] : [optionId];
    } else {
      newValue = value.includes(optionId)
        ? value.filter((id) => id !== optionId)
        : [...value, optionId];
    }

    onChange(newValue);
    if (singleSelect) {
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  // Handle select all toggle
  const handleSelectAllToggle = () => {
    if (disabled) return;

    if (selectAllChecked) {
      // Deselect all filtered options
      const filteredIds = filteredOptions.map((opt) => opt.id);
      const newValue = value.filter((id) => !filteredIds.includes(id));
      onChange(newValue);
    } else {
      // Select all filtered options
      const filteredIds = filteredOptions.map((opt) => opt.id);
      const newValue = [...new Set([...value, ...filteredIds])];
      onChange(newValue);
    }
  };

  // Get display text
  const getDisplayText = () => {
    if (loading) {
      return loadingText;
    }

    if (value.length === 0) {
      return placeholder;
    }

    if (singleSelect && value.length === 1) {
      const option = options.find((opt) => opt.id === value[0]);
      return option?.text || placeholder;
    }

    // Single "All" selection (geo filter sentinel)
    if (value.length === 1) {
      const option = options.find((opt) => opt.id === value[0]);
      const label = String(option?.text ?? "").trim().toLowerCase();
      const id = String(value[0] ?? "").trim().toLowerCase();
      if (label === "all" || id === "all" || id === "0000") {
        return option?.text || "All";
      }
    }
    
    // Check if all options are selected
    const allOptionsSelected = options.length > 0 && 
      options.every((option) => value.includes(option.id));
    
    if (allOptionsSelected) {
      return `All selected (${value.length})`;
    } else {
      return `Selected (${value.length})`;
    }
  };

  // Get selected option names for tooltip
  const getSelectedNames = () => {
    if (value.length === 0) {
      return "";
    }
    const selectedNames = value
      .map((id) => {
        const option = options.find((opt) => opt.id === id);
        return option ? option.text : null;
      })
      .filter((name) => name !== null);
    return selectedNames.join(", ");
  };

  return (
    <div
      ref={dropdownRef}
      className={`custom-multiselect ${className} ${disabled || loading ? "disabled" : ""} ${
        loading ? "loading" : ""
      } ${isOpen ? "open" : ""}`}
    >
      {/* Selected Display */}
      <div
        ref={displayRef}
        className="custom-multiselect-display"
        onClick={toggleDropdown}
        style={{ cursor: disabled || loading ? "not-allowed" : "pointer" }}
      >
        {loading && (
          <span
            className="custom-multiselect-spinner"
            aria-hidden="true"
          />
        )}
        <span
          className={`custom-multiselect-text ${loading ? "is-loading" : ""}`}
          title={!loading && value.length > 0 ? getSelectedNames() : ""}
        >
          {getDisplayText()}
        </span>
        {!loading && (
        <span className="custom-multiselect-arrow">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7 10L12 15L17 10H7Z"
              fill="#888"
            />
          </svg>
        </span>
        )}
      </div>

      {/* Dropdown Menu - Rendered via Portal */}
      {isOpen &&
        createPortal(
          <div
            ref={dropdownMenuRef}
            className="custom-multiselect-dropdown"
            style={{
              position: "absolute",
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
            }}
          >
            {/* Search Input */}
            <div className="custom-multiselect-search">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="custom-multiselect-search-input"
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            {/* Options List */}
            <div className="custom-multiselect-options">
              {/* Select All Option */}
              {showSelectAll && !singleSelect && filteredOptions.length > 0 && (
                <div
                  className="custom-multiselect-option select-all-option"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectAllToggle();
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectAllChecked}
                    onChange={handleSelectAllToggle}
                    onClick={(e) => e.stopPropagation()}
                    className="custom-multiselect-checkbox"
                  />
                  <span className="custom-multiselect-option-text">
                    {selectAllLabel}
                  </span>
                </div>
              )}

              {/* Options */}
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => {
                  const isSelected = value.includes(option.id);
                  return (
                    <div
                      key={option.id}
                      className={`custom-multiselect-option ${
                        isSelected ? "selected" : ""
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOptionToggle(option.id);
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleOptionToggle(option.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="custom-multiselect-checkbox"
                      />
                      <span className="custom-multiselect-option-text">
                        {option.text}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div className="custom-multiselect-no-results">
                  No results found
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default CustomMultiSelect;

